import { OcularMotilityData, OcularMotilityMetrics, TestType } from "types/testTypes";
import { ClinicalSummaryInterpretation, ClinicalSummaryMetric } from "./clinicalSummaryTypes";

const OCULAR_MOTILITY_THRESHOLDS = {
  pursuitGain: { mildLow: 0.68, severeLow: 0.5 },
  meanError: { mildHigh: 3.5, severeHigh: 5.5 },
  gainAsymmetry: { mildHigh: 0.28, severeHigh: 0.45 },
  errorAsymmetry: { mildHigh: 2.0, severeHigh: 3.5 },
  saccadeBurden: { mildHigh: 12, severeHigh: 20 },
  velocityMismatch: { mildHigh: 3, severeHigh: 5.5 },
  xihPatternAsymmetry: { mildHigh: 2.0, severeHigh: 3.5 },
} as const;

type OcularMotilityResultPayload = {
  rightGazePositions: unknown[];
  leftGazePositions: unknown[];
  targetPositions: unknown[];
  rightEyeErrors?: number[];
  rightEyeTangentialVelocities?: number[];
  leftEyeErrors?: number[];
  leftEyeTangentialVelocities?: number[];
  timestamps: unknown[];
} & Partial<OcularMotilityMetrics>;

type OcularMotilityInterpretationInput = {
  chartData?: OcularMotilityData[] | null;
  metrics?: OcularMotilityMetrics | null;
  testType?: TestType | null;
};

type EyeKey = "left" | "right";

const asFiniteNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value)
    ? value
    : typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))
      ? Number(value)
      : null;

export const resolvePursuitGain = (
  pursuitGain: unknown,
  meanTangentialVelocity: unknown,
  targetVelocity: unknown,
): number | null => {
  const explicitGain = asFiniteNumber(pursuitGain);
  if (typeof explicitGain === "number" && explicitGain > 0) {
    return explicitGain;
  }

  const tangentialVelocity = asFiniteNumber(meanTangentialVelocity);
  const target = asFiniteNumber(targetVelocity);

  if (
    typeof tangentialVelocity === "number" &&
    tangentialVelocity > 0 &&
    typeof target === "number" &&
    target > 0
  ) {
    return tangentialVelocity / target;
  }

  return null;
};

const getEyePosition = (point: OcularMotilityData, eye: EyeKey) =>
  eye === "left" ? point.leftGazePosition : point.rightGazePosition;

const getDistance = (
  first: { x: number; y: number } | null,
  second: { x: number; y: number } | null,
) => {
  if (!first || !second) return null;
  return Math.hypot(second.x - first.x, second.y - first.y);
};

export const deriveChartPursuitGain = (
  chartData: OcularMotilityData[] | null | undefined,
  eye: EyeKey,
): number | null => {
  if (!chartData || chartData.length < 2) return null;

  let totalEyeDistance = 0;
  let totalTargetDistance = 0;
  let validSegments = 0;

  for (let index = 1; index < chartData.length; index += 1) {
    const previousPoint = chartData[index - 1];
    const currentPoint = chartData[index];

    const targetDistance = getDistance(previousPoint.targetPosition, currentPoint.targetPosition);
    const eyeDistance = getDistance(
      getEyePosition(previousPoint, eye),
      getEyePosition(currentPoint, eye),
    );

    if (
      targetDistance === null ||
      eyeDistance === null ||
      targetDistance <= 0 ||
      !Number.isFinite(targetDistance) ||
      !Number.isFinite(eyeDistance)
    ) {
      continue;
    }

    totalTargetDistance += targetDistance;
    totalEyeDistance += eyeDistance;
    validSegments += 1;
  }

  if (validSegments < 2 || totalTargetDistance <= 0) {
    return null;
  }

  return totalEyeDistance / totalTargetDistance;
};

export const resolveOcularPursuitGain = ({
  pursuitGain,
  meanTangentialVelocity,
  targetVelocity,
  chartData,
  eye,
}: {
  pursuitGain: unknown;
  meanTangentialVelocity: unknown;
  targetVelocity: unknown;
  chartData?: OcularMotilityData[] | null;
  eye: EyeKey;
}): number | null => {
  const metricGain = resolvePursuitGain(pursuitGain, meanTangentialVelocity, targetVelocity);
  const chartGain = deriveChartPursuitGain(chartData, eye);

  if (metricGain === null) {
    return chartGain;
  }

  if (chartGain === null) {
    return metricGain;
  }

  const isClearlyUnderstatedMetric =
    metricGain <= 0.05 ||
    (chartGain >= 0.35 && metricGain < chartGain * 0.2 && chartGain - metricGain >= 0.25);

  return isClearlyUnderstatedMetric ? chartGain : metricGain;
};

const asPosition = (value: unknown): { x: number; y: number } | null => {
  if (!value || typeof value !== "object") return null;

  const x = asFiniteNumber((value as { x?: unknown }).x);
  const y = asFiniteNumber((value as { y?: unknown }).y);
  if (x === null || y === null) return null;

  return { x, y };
};

const average = (values: Array<number | null | undefined>): number | null => {
  const finiteValues = values.filter((value): value is number => typeof value === "number");
  if (!finiteValues.length) return null;
  return finiteValues.reduce((sum, value) => sum + value, 0) / finiteValues.length;
};

const formatMetric = (
  labelKey: string,
  value: number | string | null,
  unit?: string,
  decimals = 2,
): ClinicalSummaryMetric => ({
  labelKey,
  value,
  unit,
  decimals,
});

const addSeverity = (
  value: number | null,
  thresholds: { mildHigh?: number; severeHigh?: number; mildLow?: number; severeLow?: number },
): "none" | "mild" | "severe" => {
  if (value === null) return "none";

  if (typeof thresholds.severeHigh === "number" && value > thresholds.severeHigh) {
    return "severe";
  }
  if (typeof thresholds.severeLow === "number" && value < thresholds.severeLow) {
    return "severe";
  }
  if (typeof thresholds.mildHigh === "number" && value > thresholds.mildHigh) {
    return "mild";
  }
  if (typeof thresholds.mildLow === "number" && value < thresholds.mildLow) {
    return "mild";
  }

  return "none";
};

const getDirectionalPatternAsymmetry = (chartData: OcularMotilityData[] | null | undefined) => {
  if (!chartData || chartData.length < 3) return null;

  const directionBuckets: Record<string, number[]> = {};

  for (let index = 1; index < chartData.length; index += 1) {
    const previousTarget = chartData[index - 1]?.targetPosition;
    const currentTarget = chartData[index]?.targetPosition;
    if (!previousTarget || !currentTarget) continue;

    const deltaX = currentTarget.x - previousTarget.x;
    const deltaY = currentTarget.y - previousTarget.y;
    if (Math.abs(deltaX) < 0.05 && Math.abs(deltaY) < 0.05) continue;

    const direction =
      Math.abs(deltaX) >= Math.abs(deltaY)
        ? deltaX >= 0
          ? "rightward"
          : "leftward"
        : deltaY >= 0
          ? "upward"
          : "downward";

    const point = chartData[index];
    const pointError = average([point.leftEyeError, point.rightEyeError]);
    if (pointError === null) continue;

    directionBuckets[direction] = directionBuckets[direction] ?? [];
    directionBuckets[direction].push(pointError);
  }

  const directionalMeans = Object.values(directionBuckets)
    .map((values) => average(values))
    .filter((value): value is number => value !== null);

  if (directionalMeans.length < 2) return null;

  return Math.max(...directionalMeans) - Math.min(...directionalMeans);
};

const formatNumber = (value: number | null, decimals = 2, unit = "") => {
  if (value === null) return "—";
  return `${value.toFixed(decimals)}${unit}`;
};

export const reconstructOcularMotilityChartData = (
  results: OcularMotilityResultPayload | null | undefined,
): OcularMotilityData[] => {
  if (!results?.timestamps?.length) return [];

  return results.timestamps.map((timestamp, index) => ({
    rightGazePosition: asPosition(results.rightGazePositions?.[index]),
    leftGazePosition: asPosition(results.leftGazePositions?.[index]),
    targetPosition: asPosition(results.targetPositions?.[index]),
    rightEyeError: asFiniteNumber(results.rightEyeErrors?.[index]),
    rightEyeTangentialVelocity: asFiniteNumber(results.rightEyeTangentialVelocities?.[index]),
    leftEyeError: asFiniteNumber(results.leftEyeErrors?.[index]),
    leftEyeTangentialVelocity: asFiniteNumber(results.leftEyeTangentialVelocities?.[index]),
    timestamp: asFiniteNumber(timestamp) ?? index,
  }));
};

export const interpretOcularMotilitySummary = ({
  chartData,
  metrics,
  testType,
}: OcularMotilityInterpretationInput): ClinicalSummaryInterpretation | null => {
  if (!metrics) return null;

  const targetVelocity = asFiniteNumber(metrics.targetVelocity);
  const leftTangentialVelocity = asFiniteNumber(metrics.leftMeanTangentialVelocity);
  const rightTangentialVelocity = asFiniteNumber(metrics.rightMeanTangentialVelocity);
  const leftPursuitGain = resolveOcularPursuitGain({
    pursuitGain: metrics.leftPursuitGain,
    meanTangentialVelocity: leftTangentialVelocity,
    targetVelocity,
    chartData,
    eye: "left",
  });
  const rightPursuitGain = resolveOcularPursuitGain({
    pursuitGain: metrics.rightPursuitGain,
    meanTangentialVelocity: rightTangentialVelocity,
    targetVelocity,
    chartData,
    eye: "right",
  });
  const leftMeanError = asFiniteNumber(metrics.leftMeanError);
  const rightMeanError = asFiniteNumber(metrics.rightMeanError);
  const leftSaccadeCount = asFiniteNumber(metrics.leftSaccadeCount);
  const rightSaccadeCount = asFiniteNumber(metrics.rightSaccadeCount);

  const worstPursuitGain =
    [leftPursuitGain, rightPursuitGain]
      .filter((value): value is number => value !== null)
      .sort((left, right) => left - right)[0] ?? null;
  const worstMeanError =
    [leftMeanError, rightMeanError]
      .filter((value): value is number => value !== null)
      .sort((left, right) => right - left)[0] ?? null;
  const gainAsymmetry =
    leftPursuitGain !== null && rightPursuitGain !== null
      ? Math.abs(leftPursuitGain - rightPursuitGain)
      : null;
  const errorAsymmetry =
    leftMeanError !== null && rightMeanError !== null
      ? Math.abs(leftMeanError - rightMeanError)
      : null;
  const totalSaccadeBurden =
    (leftSaccadeCount ?? 0) + (rightSaccadeCount ?? 0) > 0
      ? (leftSaccadeCount ?? 0) + (rightSaccadeCount ?? 0)
      : leftSaccadeCount !== null || rightSaccadeCount !== null
        ? (leftSaccadeCount ?? 0) + (rightSaccadeCount ?? 0)
        : null;
  const velocityMismatch =
    testType === TestType.CircularPursuit && targetVelocity !== null
      ? Math.max(
          leftTangentialVelocity !== null ? Math.abs(leftTangentialVelocity - targetVelocity) : 0,
          rightTangentialVelocity !== null ? Math.abs(rightTangentialVelocity - targetVelocity) : 0,
        )
      : null;
  const xihPatternAsymmetry =
    testType === TestType.XIH ? getDirectionalPatternAsymmetry(chartData) : null;

  const severities = [
    addSeverity(worstPursuitGain, OCULAR_MOTILITY_THRESHOLDS.pursuitGain),
    addSeverity(worstMeanError, OCULAR_MOTILITY_THRESHOLDS.meanError),
    addSeverity(gainAsymmetry, OCULAR_MOTILITY_THRESHOLDS.gainAsymmetry),
    addSeverity(errorAsymmetry, OCULAR_MOTILITY_THRESHOLDS.errorAsymmetry),
    addSeverity(totalSaccadeBurden, OCULAR_MOTILITY_THRESHOLDS.saccadeBurden),
    addSeverity(velocityMismatch, OCULAR_MOTILITY_THRESHOLDS.velocityMismatch),
    addSeverity(xihPatternAsymmetry, OCULAR_MOTILITY_THRESHOLDS.xihPatternAsymmetry),
  ];

  const severeCount = severities.filter((severity) => severity === "severe").length;
  const mildCount = severities.filter((severity) => severity === "mild").length;

  const riskLevel =
    severeCount >= 2 || (severeCount >= 1 && mildCount >= 3)
      ? "high"
      : severeCount === 1 || mildCount >= 3
        ? "monitor"
        : "low";

  const testKey = testType === TestType.XIH ? "xih" : "circularPursuit";
  const summaryMetrics: ClinicalSummaryMetric[] = [
    formatMetric(
      "extraTests.ocularMotility.summary.metrics.worstPursuitGain",
      worstPursuitGain,
      "",
      2,
    ),
    formatMetric(
      "extraTests.ocularMotility.summary.metrics.worstTrackingError",
      worstMeanError,
      "°",
      2,
    ),
    formatMetric("extraTests.ocularMotility.summary.metrics.gainAsymmetry", gainAsymmetry, "", 2),
    formatMetric(
      "extraTests.ocularMotility.summary.metrics.correctiveSaccades",
      totalSaccadeBurden,
      "",
      0,
    ),
    testType === TestType.CircularPursuit
      ? formatMetric(
          "extraTests.ocularMotility.summary.metrics.speedMismatch",
          velocityMismatch,
          "°/s",
          2,
        )
      : formatMetric(
          "extraTests.ocularMotility.summary.metrics.patternAsymmetry",
          xihPatternAsymmetry ?? errorAsymmetry,
          "°",
          2,
        ),
  ];

  const keyFindings =
    testType === TestType.XIH
      ? [
          `Weaker-eye gain was ${formatNumber(worstPursuitGain, 2)}.`,
          `Mean error was ${formatNumber(worstMeanError, 2, "°")}; pattern asymmetry was ${formatNumber(xihPatternAsymmetry ?? errorAsymmetry, 2, "°")}.`,
          `Corrective eye jumps totaled ${formatNumber(totalSaccadeBurden, 0)}.`,
        ]
      : [
          `Weaker-eye gain was ${formatNumber(worstPursuitGain, 2)}.`,
          `Mean error was ${formatNumber(worstMeanError, 2, "°")}; gain asymmetry was ${formatNumber(gainAsymmetry, 2)}.`,
          `Corrective eye jumps totaled ${formatNumber(totalSaccadeBurden, 0)}${velocityMismatch !== null ? `; speed mismatch was ${formatNumber(velocityMismatch, 2, "°/s")}.` : "."}`,
        ];

  const interpretationNotes =
    testType === TestType.XIH
      ? [
          "Higher gain and lower error usually mean steadier tracking.",
          "Small directional differences can happen in normal testing.",
          "Pattern asymmetry matters more when gain falls or error rises too.",
        ]
      : [
          "Higher gain and lower error usually mean smoother tracking.",
          "Small asymmetries can happen in normal testing.",
          "Corrective eye jumps matter more when gain is also low or error is higher.",
        ];

  return {
    riskLevel,
    titleKey: "extraTests.ocularMotility.summary.title",
    explainerKey: `extraTests.ocularMotility.summary.explainer.${testKey}`,
    reasonKey: `extraTests.ocularMotility.summary.reasons.${testKey}.${riskLevel}`,
    keyFindings,
    interpretationNotes,
    metrics: summaryMetrics,
  };
};
